import {createAsyncThunk, createAction} from '@reduxjs/toolkit';
import getReservations from 'api/reservations/get-reservations';
import getReservationsDailyCounts from 'api/reservations/get-reservations-daily-counts';
import getReservationsDailySegmented from 'api/reservations/get-reservations-daily-segmented';
import getReservationsDaily from 'api/reservations/get-reservations-daily';
import {RELATIVE_DAYS} from 'constants/index';

/** @namespace Reservations.Actions */

/**
 * Thunk that fetches all reservations for an array of facility IDs.
 */
export const getReservationsData = createAsyncThunk(
    'reservations/getReservations',
    /**
     @param {Object} params - Parameters to pass to the API.
     @param {array} params.facilityIds - IDs of the selected facilities
     @param {string|Dayjs} params.dateFrom - Start of date range to return reservations for
     @param {string|Dayjs} params.dateTo - End of date range to return reservations for
     @param {string} [params.reservationTypes] - Types of reservations to include.
     @param {string} [params.statuses] - Statuses of the reservations
     @param {string} [params.searchText] - Search text to filter reservations.
     @param {number} [params.offset] - Offset for pagination of reservations.
     @param {Function} [params.handleAbortSignal] - A function to handle aborting the request.
     @param {AbortSignal} [params.abortSignal ]- The cancel token to cancel the request.
     */
    async (
        {
            facilityIds,
            dateFrom,
            dateTo,
            eventId,
            reservationTypes = 'all',
            statuses = 'all',
            searchText,
            offset = 0,
            handleAbortSignal,
            abortSignal,
        },
        {rejectWithValue, signal}
    ) => {
        try {
            if (handleAbortSignal) {
                handleAbortSignal(signal);
            }

            const response = await getReservations(
                {
                    facilityIds,
                    eventId,
                    dateFrom,
                    dateTo,
                    reservationTypes,
                    statuses,
                    searchText,
                    offset,
                },
                {signal: abortSignal}
            );

            return response;
        } catch (error) {
            return rejectWithValue(error);
        }
    }
);

/**
 * Thunk to get the daily counts for an operator's reservations
 * @param {Object} params - params to pass to the api
 * @param {string} params.facilityId - id of the facility
 */
export const getReservationsDailyCountsData = createAsyncThunk(
    'reservations/getReservationsDailyCountsData',
    async ({facilityId}, {rejectWithValue}) => {
        try {
            const response = await getReservationsDailyCounts(facilityId);

            return response;
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

/**
 * Thunk to get the daily segments for an operator's reservations
 * @param {Object} params - params to pass to the api
 * @param {string} params.facilityId - id of the facility
 * @param {string} params.date - date for segments
 */
export const getReservationsDailySegmentedData = createAsyncThunk(
    'reservations/getReservationsDailySegmentedData',
    async ({facilityId, date}, {rejectWithValue}) => {
        try {
            const response = await getReservationsDailySegmented(
                facilityId,
                date && date
            );

            return response;
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

/**
 * Thunk to get the operator's daily reservations.
 * @param {Object} params - Parameters to pass to the API.
 * @param {string} params.facilityId - ID of the facility.
 * @param {string} params.reservationTypes - Types of reservations to include.
 * @param {string} params.scope - Scope of the reservations
 * @param {string} params.statuses - Statuses of the reservations
 * @param {string} params.searchText - Search text to filter reservations.
 * @param {number} params.offset - Offset for pagination of reservations.
 * @param {Function} params.handleAbortSignal - A function to handle aborting the request.
 * @param {AbortSignal} params.abortSignal - The cancel token to cancel the request.
 * @returns {} A promise that resolves to the fetched data or rejects with an error.
 */
export const getReservationsDailyData = createAsyncThunk(
    'reservations/getReservationsDailyData',
    async (
        {
            facilityId,
            reservationTypes = 'all',
            scope = 'all',
            statuses = 'all',
            searchText,
            offset = 0,
            handleAbortSignal,
            abortSignal,
        },
        {rejectWithValue, signal}
    ) => {
        try {
            if (handleAbortSignal) {
                handleAbortSignal(signal);
            }

            const response = await getReservationsDaily(
                {
                    facilityId,
                    reservationTypes,
                    statuses,
                    scope,
                    searchText,
                    offset,
                },
                {signal: abortSignal}
            );

            return response;
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

/**
 * Thunk to get the operator's daily reservations.
 * @param {Object} params - Parameters to pass to the API.
 * @param {string} params.facilityId - ID of the facility.
 * @param {string} params.date - date for chart segments
 * @param {string} params.reservationTypes - Types of reservations to include.
 * @param {string} params.scope - Scope of the reservations
 * @param {string} params.statuses - Statuses of the reservations
 * @param {string} params.searchText - Search text to filter reservations.
 * @param {number} params.offset - Offset for pagination of reservations.
 * @param {Function} params.handleAbortSignal - A function to handle aborting the request.
 * @param {AbortSignal} params.abortSignal - The cancel token to cancel the request.
 * @returns {} A promise that resolves to the fetched data or rejects with an error.
 */
export const getTodaysReservationsData = createAsyncThunk(
    'reservations/getTodaysReservationsData',
    async (
        {
            relativeDay,
            facilityId,
            date,
            reservationTypes = 'all',
            scope = 'all',
            statuses = 'all',
            searchText,
            offset = 0,
            handleAbortSignal,
            abortSignal,
        },
        {rejectWithValue, signal, dispatch}
    ) => {
        try {
            if (handleAbortSignal) {
                handleAbortSignal(signal);
            }
            const dailyCountsDataPayload = {
                facilityId,
                handleAbortSignal,
                abortSignal,
            };
            const dailySegmentedDataPayload = {
                facilityId,
                ...(date && {date}),
            };
            const dailyDataPayload = {
                facilityId,
                reservationTypes,
                statuses,
                scope,
                searchText,
                offset,
                handleAbortSignal,
                abortSignal,
            };

            const dailyCountsPromise =
                relativeDay === RELATIVE_DAYS.TODAY
                    ? dispatch(
                          getReservationsDailyCountsData(dailyCountsDataPayload)
                      )
                    : null;
            const dailySegmentedPromise = dispatch(
                getReservationsDailySegmentedData(dailySegmentedDataPayload)
            );
            const dailyPromise = dispatch(
                getReservationsDailyData(dailyDataPayload)
            );

            await Promise.all([
                dailyCountsPromise,
                dailySegmentedPromise,
                dailyPromise,
            ]);

            return;
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

/**
 * Action to reset the state of the segmented chart data
 */
export const resetSegementedState = createAction(
    'reservations/resetSegementedState'
);

/**
 * Action to handle the state of the transient data
 */
export const setTransientLegend = createAction(
    'reservations/setTransientLegend'
);

/**
 * Action to handle the state of the event data
 */
export const setEventLegend = createAction('reservations/setEventLegend');

/**
 * Action to handle the state of the monthly data
 */
export const setMonthlyLegend = createAction('reservations/setMonthlyLegend');

/**
 * Action to handle the state of the reservations filters
 */
export const setReservationsFilters = createAction(
    'reservations/setReservationsFilters',
    (filters) => {
        return {
            payload: filters,
        };
    }
);
